import { entitySelectionPayload } from '../../../shared/utils/entitySelection';
import {
  readDocumentsStorage,
  writeDocumentsStorage,
} from '@/modules/documents/services/documents.storage';
import {
  getStoredSessionToken,
  isSessionExpiredPayload,
  isSessionRefreshRequiredPayload,
} from '../../../app/services/session.service';
import {
  hydrateDocumentRecord,
  validateContractForm,
  validateMandateForm,
} from '@/modules/documents/utils';
import { inferMimeType } from '../utils/documents.mappers';
import {
  getDocumentPreviewKind,
  resolveDocumentPreviewMimeType,
} from '../utils/documents.helpers';
import { allowLocalDocumentsSnapshotFallback } from '../../../app/config/runtime-environment';

const DOCUMENTS_API_BASE_URL = (process.env.REACT_APP_DOCUMENTS_API_BASE_URL || '').replace(/\/$/, '');
const DOCUMENTS_API_PATH = '/api/documents';
const CUSTOM_FIELDS_API_PATH = '/api/documents/custom-fields';
const DOCUMENT_TEXT_PREVIEW_MAX_CHARS = 200000;
const TENANT_DB_ERROR_CODES = new Set([
  'tenant_database_not_ready',
  'tenant_database_unavailable',
  'tenant_database_metadata_missing',
]);

function getDocumentsApiUrl(path = '') {
  const resolvedPath = path || DOCUMENTS_API_PATH;
  return `${DOCUMENTS_API_BASE_URL}${resolvedPath}`;
}

function getDocumentEndpointUrl(documentId, action) {
  if (documentId === null || documentId === undefined || documentId === '') {
    return '';
  }

  return getDocumentsApiUrl(`${DOCUMENTS_API_PATH}/${encodeURIComponent(documentId)}/${action}`);
}

function isAuthFailure(status) {
  return status === 401 || status === 403;
}

async function isModuleDisabledResponse(response) {
  if (response.status !== 403) return false;

  try {
    const payload = await response.clone().json();
    return payload?.error === 'module_disabled';
  } catch {
    return false;
  }
}

async function isCapabilityDeniedResponse(response) {
  if (response.status !== 403) return null;

  try {
    const payload = await response.clone().json();
    return payload?.error === 'capability_denied' ? payload : null;
  } catch {
    return null;
  }
}

async function readErrorPayload(response) {
  try {
    return await response.clone().json();
  } catch {
    return null;
  }
}

function mapTenantDatabaseError(payload) {
  if (!TENANT_DB_ERROR_CODES.has(payload?.error)) {
    return null;
  }

  return {
    error:
      payload.error === 'tenant_database_not_ready'
        ? 'DOCUMENTS_TENANT_DATABASE_NOT_READY'
        : payload.error === 'tenant_database_metadata_missing'
          ? 'DOCUMENTS_TENANT_DATABASE_METADATA_MISSING'
          : 'DOCUMENTS_TENANT_DATABASE_UNAVAILABLE',
    message: payload?.message || 'Database tenant non disponibile per il modulo documenti.',
  };
}

function buildAuthFailureResult(payload, fallbackMessage = 'Sessione non autorizzata per il backend documenti.') {
  if (isSessionRefreshRequiredPayload(payload)) {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENTS_SESSION_REFRESH_REQUIRED',
      message: payload?.message || 'Sessione non aggiornata. Effettua di nuovo l\'accesso.',
    });
  }

  if (isSessionExpiredPayload(payload)) {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENTS_SESSION_EXPIRED',
      message: payload?.message || 'Sessione scaduta. Effettua di nuovo il login.',
    });
  }

  return buildMutationResult({
    ok: false,
    error: 'DOCUMENTS_AUTH_REQUIRED',
    message: fallbackMessage,
  });
}

function getDocumentsAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();

  if (!token) {
    return extraHeaders;
  }

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

function getDocumentsBearerToken() {
  return getStoredSessionToken();
}

function isValidStoredDocument(document) {
  return Boolean(
    document &&
      (typeof document.id === 'string' || typeof document.id === 'number') &&
      typeof document.name === 'string' &&
      typeof document.uploadDate === 'string' &&
      typeof document.uploadedBy === 'string' &&
      typeof document.client === 'string' &&
      typeof document.category === 'string' &&
      typeof document.status === 'string' &&
      typeof document.fileName === 'string' &&
      typeof document.mimeType === 'string' &&
      typeof document.downloadUrl === 'string'
  );
}

function readStoredDocuments() {
  const rawPayload = readDocumentsStorage();
  if (!rawPayload) return null;

  try {
    const parsed = JSON.parse(rawPayload);
    if (!Array.isArray(parsed) || !parsed.every(isValidStoredDocument)) {
      return null;
    }

    return parsed.map(hydrateDocumentRecord);
  } catch {
    return null;
  }
}

function markDocumentsAsStaleSnapshot(documents) {
  return documents.map((document) => ({
    ...document,
    isSnapshotStale: true,
    dataSource: 'snapshot',
  }));
}

function buildMutationResult({ ok, data = null, error = null, message = '' }) {
  return {
    ok,
    data,
    error,
    message,
  };
}

function buildJsonMutationOptions(payload) {
  return {
    method: 'POST',
    headers: getDocumentsAuthHeaders({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(payload),
  };
}

function getDocumentCustomFieldsApiUrl(formKey, path = '') {
  return getDocumentsApiUrl(`${CUSTOM_FIELDS_API_PATH}/${encodeURIComponent(formKey)}${path}`);
}

export async function listDocumentsCustomFields(formKey) {
  const response = await fetch(getDocumentCustomFieldsApiUrl(formKey), { headers: getDocumentsAuthHeaders() });
  const payload = await readErrorPayload(response);
  if (!response.ok) throw new Error(payload?.message || 'Campi personalizzati non disponibili.');
  return Array.isArray(payload) ? payload : [];
}

export async function createDocumentsCustomField(definition, formKey) {
  const response = await fetch(getDocumentCustomFieldsApiUrl(formKey), {
    method: 'POST',
    headers: getDocumentsAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(definition),
  });
  const payload = await readErrorPayload(response);
  return response.ok
    ? buildMutationResult({ ok: true, data: payload })
    : buildMutationResult({ ok: false, error: 'CUSTOM_FIELD_CREATE_FAILED', message: payload?.message || 'Impossibile salvare il campo.' });
}

export async function archiveDocumentsCustomField(id, formKey) {
  const response = await fetch(getDocumentCustomFieldsApiUrl(formKey, `/${encodeURIComponent(id)}`), {
    method: 'DELETE',
    headers: getDocumentsAuthHeaders(),
  });
  const payload = await readErrorPayload(response);
  return response.ok
    ? buildMutationResult({ ok: true })
    : buildMutationResult({ ok: false, error: 'CUSTOM_FIELD_ARCHIVE_FAILED', message: payload?.message || 'Impossibile archiviare il campo.' });
}

function formatDocumentSize(size) {
  if (typeof size === 'number' && Number.isFinite(size)) {
    return `${(size / 1024 / 1024).toFixed(2)} MB`;
  }

  return typeof size === 'string' && size ? size : 'N/D';
}

function resolveDocumentDownloadUrl(downloadUrl) {
  if (!downloadUrl) return '';
  if (downloadUrl.startsWith('data:')) return '';
  if (/^https?:\/\//.test(downloadUrl)) {
    return downloadUrl;
  }

  return getDocumentsApiUrl(downloadUrl);
}

function resolveDocumentPreviewUrl(previewUrl, document) {
  if (document?.id != null) {
    return getDocumentEndpointUrl(document.id, 'preview');
  }

  return previewUrl ? resolveDocumentDownloadUrl(previewUrl) : '';
}

function resolveApiDocumentMimeType(document) {
  const mimeType = typeof document?.mimeType === 'string' ? document.mimeType.trim().toLowerCase() : '';
  if (
    !mimeType ||
    mimeType === 'application/octet-stream' ||
    mimeType === 'binary/octet-stream' ||
    mimeType === 'application/unknown'
  ) {
    return inferMimeType(document?.fileName || document?.name, document?.type);
  }

  return document.mimeType;
}

export function mapApiDocumentToDocument(document) {
  return hydrateDocumentRecord({
    ...document,
    mimeType: resolveApiDocumentMimeType(document),
    clientId: document?.clientId ?? '',
    assignmentId: document?.assignmentId ?? null,
    linkedModule: document?.linkedModule || document?.sourceModule || '',
    sourceModule: document?.sourceModule || document?.linkedModule || '',
    linkedEntityType: document?.linkedEntityType || '',
    linkedEntityId: document?.linkedEntityId ?? '',
    relations: Array.isArray(document?.relations) ? document.relations.map((item) => ({
      entityType: item.entityType, entityId: String(item.entityId), label: item.label || '',
    })) : [],
    size: formatDocumentSize(document.size),
    downloadUrl:
      document?.id != null
        ? getDocumentEndpointUrl(document.id, 'download')
        : resolveDocumentDownloadUrl(document.downloadUrl),
    previewUrl: resolveDocumentPreviewUrl(document.previewUrl, document),
  });
}

export function getFallbackDocuments() {
  const cachedDocuments = readStoredDocuments();
  return cachedDocuments ? cachedDocuments.map((document) => ({ ...document })) : [];
}

function appendQueryParam(query, key, value) {
  if (value !== null && value !== undefined && value !== '') {
    query.set(key, value);
  }
}

export async function listDocumentsResult({
  assignmentId,
  linkedModule,
  sourceModule,
  linkedEntityType,
  linkedEntityId,
} = {}) {
  try {
    const queryParams = new URLSearchParams();
    appendQueryParam(queryParams, 'assignmentId', assignmentId);
    appendQueryParam(queryParams, 'linkedModule', linkedModule || sourceModule);
    appendQueryParam(queryParams, 'linkedEntityType', linkedEntityType);
    appendQueryParam(queryParams, 'linkedEntityId', linkedEntityId);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await fetch(getDocumentsApiUrl(`${DOCUMENTS_API_PATH}${query}`), {
      headers: getDocumentsAuthHeaders(),
    });

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        data: {
          documents: [],
          source: 'none',
          hasCachedSnapshot: false,
          documentsSnapshotStale: false,
        },
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENTS_MODULE_DISABLED',
        data: {
          documents: [],
          source: 'none',
          hasCachedSnapshot: false,
          documentsSnapshotStale: false,
        },
        message: 'Modulo documenti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return {
        ...buildAuthFailureResult(tenantDbPayload),
        data: {
          documents: [],
          source: 'none',
          hasCachedSnapshot: false,
          documentsSnapshotStale: false,
        },
      };
    }

    if (!response.ok) {
      throw new Error(`DOCUMENTS_API_LIST_FAILED_${response.status}`);
    }

    const payload = await response.json();
    if (!Array.isArray(payload)) {
      throw new Error('DOCUMENTS_API_LIST_INVALID_PAYLOAD');
    }

    const documents = payload.map((document) => ({
      ...mapApiDocumentToDocument(document),
      isSnapshotStale: false,
      dataSource: 'backend',
    }));
    persistDocuments(documents);

    return buildMutationResult({
      ok: true,
      data: {
        documents,
        source: 'backend',
        hasCachedSnapshot: false,
        documentsSnapshotStale: false,
      },
    });
  } catch {
    const cachedDocuments = getFallbackDocuments();
    const staleSnapshot = markDocumentsAsStaleSnapshot(cachedDocuments);
    const allowSnapshotFallback = allowLocalDocumentsSnapshotFallback();
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENTS_API_UNAVAILABLE',
      data: {
        documents: allowSnapshotFallback ? staleSnapshot : [],
        source: allowSnapshotFallback && cachedDocuments.length > 0 ? 'cache' : 'none',
        hasCachedSnapshot: allowSnapshotFallback && cachedDocuments.length > 0,
        documentsSnapshotStale: allowSnapshotFallback && cachedDocuments.length > 0,
      },
      message: allowSnapshotFallback && cachedDocuments.length > 0
        ? 'Backend documenti non disponibile. Mostro uno snapshot locale non aggiornato della library.'
        : 'Backend documenti non disponibile. Nessun documento disponibile.',
    });
  }
}

export async function getDocuments() {
  const result = await listDocumentsResult();
  return result.data.documents;
}

function resolveDocumentBinaryUrl(document, mode = 'download') {
  if (!document || document.isSnapshotStale === true) {
    return '';
  }

  if (mode === 'preview') {
    const previewKind = getDocumentPreviewKind(document);
    if (previewKind === 'pdf') {
      return document.previewUrl || document.downloadUrl || '';
    }
    if (previewKind === 'image' || previewKind === 'text') {
      return document.downloadUrl || '';
    }
    return '';
  }

  return document.downloadUrl || '';
}

function mapPreviewFailureMessage(status, payload) {
  const backendMessage = payload?.message || '';

  if (status === 400) {
    return backendMessage || 'Anteprima disponibile solo per PDF.';
  }

  if (status === 401 || status === 403) {
    return backendMessage || 'Sessione non autorizzata per l\'anteprima documento.';
  }

  if (status === 404) {
    if (backendMessage === 'Stored document file not found') {
      return 'File del documento non trovato nello storage.';
    }

    return backendMessage || 'Documento non trovato o non accessibile.';
  }

  return backendMessage || 'Anteprima documento non disponibile.';
}

function buildUnsupportedPreviewResult() {
  return buildMutationResult({
    ok: false,
    error: 'DOCUMENT_PREVIEW_UNSUPPORTED',
    message: 'Anteprima non disponibile per questo formato. Scarica il documento.',
  });
}

function readBlobAsText(blob) {
  if (blob && typeof blob.text === 'function') {
    return blob.text();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

async function fetchDocumentBinary(document, mode = 'download') {
  if (!document || document.isSnapshotStale === true) {
    return buildMutationResult({
      ok: false,
      error: mode === 'preview' ? 'DOCUMENT_PREVIEW_UNAVAILABLE' : 'DOCUMENT_DOWNLOAD_UNAVAILABLE',
      message:
        mode === 'preview'
          ? 'Anteprima documento non disponibile.'
          : 'Download documento non disponibile.',
    });
  }

  const previewKind = mode === 'preview' ? getDocumentPreviewKind(document) : '';
  if (mode === 'preview' && previewKind === 'unsupported') {
    return buildUnsupportedPreviewResult();
  }
  const endpointUrl =
    mode === 'preview'
      ? getDocumentEndpointUrl(document?.id, 'preview')
      : getDocumentEndpointUrl(document?.id, 'download');
  const documentUrl = endpointUrl || resolveDocumentBinaryUrl(document, mode);
  if (!documentUrl) {
    return buildMutationResult({
      ok: false,
      error: mode === 'preview' ? 'DOCUMENT_PREVIEW_UNAVAILABLE' : 'DOCUMENT_DOWNLOAD_UNAVAILABLE',
      message:
        mode === 'preview'
          ? 'Anteprima documento non disponibile.'
          : 'Download documento non disponibile.',
    });
  }

  if (documentUrl.startsWith('data:')) {
    return buildMutationResult({
      ok: false,
      error: mode === 'preview' ? 'DOCUMENT_PREVIEW_UNAVAILABLE' : 'DOCUMENT_DOWNLOAD_UNAVAILABLE',
      message:
        mode === 'preview'
          ? 'Anteprima documento disponibile solo dal backend.'
          : 'Download documento disponibile solo dal backend.',
    });
  }

  try {
    const response = await fetch(documentUrl, {
      headers: getDocumentsAuthHeaders(),
    });
    const errorPayload = !response.ok ? await readErrorPayload(response) : null;

    if (isAuthFailure(response.status)) {
      if (mode === 'preview') {
        return buildMutationResult({
          ok: false,
          error: 'DOCUMENTS_AUTH_REQUIRED',
          message: mapPreviewFailureMessage(response.status, errorPayload),
        });
      }

      return buildAuthFailureResult(errorPayload);
    }

    if (mode === 'preview' && response.status === 400) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENT_PREVIEW_UNAVAILABLE',
        message: mapPreviewFailureMessage(response.status, errorPayload),
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: mode === 'preview' ? 'DOCUMENT_PREVIEW_UNAVAILABLE' : 'DOCUMENT_DOWNLOAD_UNAVAILABLE',
        message:
          mode === 'preview'
            ? mapPreviewFailureMessage(response.status, errorPayload)
            : 'Download documento non disponibile.',
      });
    }

    if (!response.ok) {
      throw new Error(`DOCUMENT_BINARY_FETCH_FAILED_${response.status}`);
    }

    const blob = await response.blob();
    return buildMutationResult({
      ok: true,
      data: {
        blob,
      },
      message: '',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENTS_API_UNAVAILABLE',
      message:
        mode === 'preview'
          ? 'Anteprima documento non disponibile dal backend.'
          : 'Download documento non disponibile dal backend.',
    });
  }
}

export async function getDocumentPreviewObjectUrl(document) {
  const previewKind = getDocumentPreviewKind(document);
  const mimeType = resolveDocumentPreviewMimeType(document);

  if (previewKind === 'unsupported') {
    return buildUnsupportedPreviewResult();
  }

  const result = await fetchDocumentBinary(document, 'preview');
  if (!result.ok) {
    return result;
  }

  const responseBlob = result.data.blob;
  const blob = responseBlob.type
    ? responseBlob
    : new Blob([responseBlob], { type: mimeType || 'application/octet-stream' });
  let textContent = '';
  let isTextTruncated = false;

  if (previewKind === 'text') {
    const rawText = await readBlobAsText(blob);
    isTextTruncated = rawText.length > DOCUMENT_TEXT_PREVIEW_MAX_CHARS;
    textContent = isTextTruncated
      ? rawText.slice(0, DOCUMENT_TEXT_PREVIEW_MAX_CHARS)
      : rawText;
  }

  return buildMutationResult({
    ok: true,
    data: {
      objectUrl: window.URL.createObjectURL(blob),
      previewKind,
      mimeType: blob.type || mimeType,
      textContent,
      isTextTruncated,
    },
    message: '',
  });
}

export async function openDocumentPreviewInNewTab(document) {
  const result = await getDocumentPreviewObjectUrl(document);
  if (!result.ok) {
    return result;
  }

  if (!result.data.objectUrl) {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENT_PREVIEW_UNAVAILABLE',
      message: 'Anteprima documento non disponibile.',
    });
  }

  window.open(result.data.objectUrl, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => {
    window.URL.revokeObjectURL(result.data.objectUrl);
  }, 60000);
  return buildMutationResult({
    ok: true,
    data: result.data,
    message: 'Anteprima aperta.',
  });
}

export async function downloadDocumentFileAuthenticated(document) {
  const result = await fetchDocumentBinary(document, 'download');
  if (!result.ok) {
    return result;
  }

  const objectUrl = window.URL.createObjectURL(result.data.blob);
  const link = window.document.createElement('a');
  link.href = objectUrl;
  link.download = document?.fileName || document?.name || 'documento';
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
  window.setTimeout(() => {
    window.URL.revokeObjectURL(objectUrl);
  }, 0);

  return buildMutationResult({
    ok: true,
    data: {
      objectUrl,
    },
    message: 'Download avviato.',
  });
}

export function getDocumentClients() {
  return [];
}

export function getDocumentStaff() {
  return [];
}

export async function uploadDocument({ file, form }) {
  const resolvedClientId = form.clientId == null ? '' : String(form.clientId).trim();
  const resolvedAssignmentId = form.assignmentId == null ? '' : String(form.assignmentId).trim();
  const resolvedLinkedModule = form.linkedModule || form.sourceModule || '';
  const resolvedLinkedEntityId =
    form.linkedEntityId === null || form.linkedEntityId === undefined
      ? ''
      : String(form.linkedEntityId).trim();
  const resolvedClient = form.client ? String(form.client).trim() : '';

  const formData = new FormData();
  formData.append('file', file);
  formData.append(
    'metadata',
    new Blob(
      [
        JSON.stringify({
          name: String(form.name || '').trim() || file.name,
          description: String(form.description || '').trim(),
          documentDate: form.documentDate || null,
          uploadedBy: form.uploadedBy,
          client: resolvedClient,
          clientId: resolvedClientId || null,
          assignmentId: resolvedAssignmentId || null,
          linkedModule: resolvedLinkedModule || null,
          linkedEntityType: form.linkedEntityType || null,
          linkedEntityId: resolvedLinkedEntityId || null,
          relations: Array.isArray(form.relations) ? form.relations : [],
          category: form.category,
          status: 'in_revisione',
          dueDate: form.dueDate || null,
        }),
      ],
      { type: 'application/json' }
    )
  );

  try {
    const response = await fetch(getDocumentsApiUrl(DOCUMENTS_API_PATH), {
      method: 'POST',
      headers: getDocumentsAuthHeaders(),
      body: formData,
    });

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENTS_MODULE_DISABLED',
        message: 'Modulo documenti non abilitato per questo workspace.',
      });
    }

    const capabilityDeniedPayload = await isCapabilityDeniedResponse(response);
    if (capabilityDeniedPayload?.capability === 'canManageDocumentDestructiveActions') {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENT_DELETE_CAPABILITY_DENIED',
        message: capabilityDeniedPayload.message || 'Non hai i permessi per eliminare documenti.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildAuthFailureResult(tenantDbPayload);
    }

    if (!response.ok) {
      throw new Error(`DOCUMENTS_API_UPLOAD_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        document: mapApiDocumentToDocument(payload),
      },
      message: 'Documento caricato con successo!',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENTS_API_UNAVAILABLE',
      message: 'Backend documenti non disponibile. Upload non completato.',
    });
  }
}

export function persistDocuments(documents) {
  const normalizedDocuments = documents.map(hydrateDocumentRecord);

  try {
    writeDocumentsStorage(JSON.stringify(normalizedDocuments));
  } catch (error) {
    return buildMutationResult({
      ok: false,
      data: {
        documents: normalizedDocuments,
      },
      error:
        error?.message === 'DOCUMENTS_STORAGE_UNAVAILABLE'
          ? 'DOCUMENTS_STORAGE_UNAVAILABLE'
          : 'DOCUMENTS_STORAGE_WRITE_FAILED',
      message:
        error?.message === 'DOCUMENTS_STORAGE_UNAVAILABLE'
          ? 'Storage locale non disponibile.'
          : 'Salvataggio locale dei documenti non riuscito.',
    });
  }

  return buildMutationResult({
    ok: true,
    data: {
      documents: normalizedDocuments,
    },
    message: 'Documenti persistiti con successo.',
  });
}

export async function updateDocumentMetadata(documentId, metadata) {
  const resolvedClientId = metadata?.clientId == null || metadata.clientId === ''
    ? null
    : Number(metadata.clientId);
  const resolvedAssignmentId = metadata?.assignmentId == null || metadata.assignmentId === ''
    ? null
    : Number(metadata.assignmentId);
  const payload = {
    name: String(metadata?.name || '').trim(),
    ...(Object.prototype.hasOwnProperty.call(metadata || {}, 'description') ? { description: String(metadata.description || '').trim() } : {}),
    ...(Object.prototype.hasOwnProperty.call(metadata || {}, 'documentDate') ? { documentDate: metadata.documentDate || null } : {}),
    uploadedBy: String(metadata?.uploadedBy || '').trim(),
    client: String(metadata?.client || '').trim(),
    clientId: Number.isFinite(resolvedClientId) ? resolvedClientId : null,
    assignmentId: Number.isFinite(resolvedAssignmentId) ? resolvedAssignmentId : null,
    linkedModule: metadata?.linkedModule || metadata?.sourceModule || null,
    linkedEntityType: metadata?.linkedEntityType || null,
    linkedEntityId:
      metadata?.linkedEntityId === null || metadata?.linkedEntityId === undefined
        ? null
        : String(metadata.linkedEntityId),
    relations: Array.isArray(metadata?.relations) ? metadata.relations : [],
    category: String(metadata?.category || '').trim(),
    status: String(metadata?.status || '').trim(),
    ...(Object.prototype.hasOwnProperty.call(metadata || {}, 'dueDate') ? { dueDate: metadata.dueDate || null } : {}),
  };

  try {
    const response = await fetch(getDocumentsApiUrl(`${DOCUMENTS_API_PATH}/${documentId}`), {
      method: 'PUT',
      headers: getDocumentsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENTS_MODULE_DISABLED',
        message: 'Modulo documenti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildAuthFailureResult(tenantDbPayload);
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENT_NOT_FOUND',
        message: 'Documento non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`DOCUMENTS_API_UPDATE_FAILED_${response.status}`);
    }

    const updatedDocument = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        document: mapApiDocumentToDocument(updatedDocument),
      },
      message: 'Documento aggiornato.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENTS_API_UNAVAILABLE',
      message: 'Backend documenti non disponibile. Modifica non completata.',
    });
  }
}

export async function deleteDocument(documentId) {
  try {
    const response = await fetch(getDocumentsApiUrl(`${DOCUMENTS_API_PATH}/${documentId}`), {
      method: 'DELETE',
      headers: getDocumentsAuthHeaders(),
    });

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENTS_MODULE_DISABLED',
        message: 'Modulo documenti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildAuthFailureResult(tenantDbPayload);
    }

    if (response.status === 403) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENT_DELETE_FORBIDDEN',
        message: 'Non hai i permessi per eliminare questo documento.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENT_NOT_FOUND',
        message: 'Documento non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`DOCUMENTS_API_DELETE_FAILED_${response.status}`);
    }

    return buildMutationResult({
      ok: true,
      data: { documentId },
      message: 'Documento eliminato con successo.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENTS_API_UNAVAILABLE',
      message: 'Backend documenti non disponibile. Eliminazione non completata.',
    });
  }
}

function mapApiDocumentNoteToNote(note) {
  return {
    id: note?.id || '',
    documentId: note?.documentId ?? '',
    parentId: note?.parentId ?? null,
    authorUserId: note?.authorUserId || '',
    linkedWorkspaceUserId: note?.linkedWorkspaceUserId || '',
    authorFullName: note?.authorFullName || note?.authorDisplayName || '',
    authorName: note?.authorName || note?.authorUserId || 'Utente',
    authorEmail: note?.authorEmail || note?.email || '',
    body: note?.body || '',
    createdAt: note?.createdAt || '',
  };
}

export async function getDocumentNotes(documentId) {
  if (!getDocumentsBearerToken()) {
    return buildAuthFailureResult(null, 'Sessione non autorizzata per le note documento.');
  }

  try {
    const response = await fetch(getDocumentsApiUrl(`${DOCUMENTS_API_PATH}/${documentId}/notes`), {
      headers: getDocumentsAuthHeaders(),
    });

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENTS_MODULE_DISABLED',
        message: 'Modulo documenti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildAuthFailureResult(tenantDbPayload);
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENT_NOT_FOUND',
        message: 'Documento non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`DOCUMENT_NOTES_LIST_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        notes: Array.isArray(payload) ? payload.map(mapApiDocumentNoteToNote) : [],
      },
      message: '',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENTS_API_UNAVAILABLE',
      message: 'Backend documenti non disponibile. Note non caricate.',
    });
  }
}

export async function createDocumentNote(documentId, body, parentId = null) {
  const trimmedBody = String(body || '').trim();
  if (!trimmedBody) {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENT_NOTE_EMPTY',
      message: 'Inserisci una nota prima di salvare.',
    });
  }

  if (!getDocumentsBearerToken()) {
    return buildAuthFailureResult(null, 'Sessione non autorizzata per le note documento.');
  }

  try {
    const response = await fetch(getDocumentsApiUrl(`${DOCUMENTS_API_PATH}/${documentId}/notes`), {
      method: 'POST',
      headers: getDocumentsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        body: trimmedBody,
        ...(parentId !== null && parentId !== undefined ? { parentId } : {}),
      }),
    });

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENTS_MODULE_DISABLED',
        message: 'Modulo documenti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildAuthFailureResult(tenantDbPayload);
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENT_NOT_FOUND',
        message: 'Documento non trovato.',
      });
    }

    if (response.status === 400) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENT_NOTE_INVALID',
        message: 'Inserisci una nota valida.',
      });
    }

    if (!response.ok) {
      throw new Error(`DOCUMENT_NOTE_CREATE_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        note: mapApiDocumentNoteToNote(payload),
      },
      message: 'Nota aggiunta.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENTS_API_UNAVAILABLE',
      message: 'Backend documenti non disponibile. Nota non salvata.',
    });
  }
}

export async function deleteDocumentNote(documentId, noteId) {
  if (!documentId || !noteId) {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENT_NOTE_DELETE_INVALID',
      message: 'Nota documento non valida.',
    });
  }

  if (!getDocumentsBearerToken()) {
    return buildAuthFailureResult(null, 'Sessione non autorizzata per le note documento.');
  }

  try {
    const response = await fetch(getDocumentsApiUrl(`${DOCUMENTS_API_PATH}/${documentId}/notes/${noteId}`), {
      method: 'DELETE',
      headers: getDocumentsAuthHeaders(),
    });

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENTS_MODULE_DISABLED',
        message: 'Modulo documenti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildAuthFailureResult(tenantDbPayload);
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENT_NOTE_NOT_FOUND',
        message: 'Nota documento non trovata.',
      });
    }

    if (!response.ok) {
      throw new Error(`DOCUMENT_NOTE_DELETE_FAILED_${response.status}`);
    }

    return buildMutationResult({
      ok: true,
      data: {},
      message: 'Nota eliminata.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENTS_API_UNAVAILABLE',
      message: 'Backend documenti non disponibile. Nota non eliminata.',
    });
  }
}

export async function createMandateDocument(form) {
  const validationError = validateMandateForm(form);
  if (validationError) {
    return buildMutationResult({
      ok: false,
      error: 'MANDATE_VALIDATION_ERROR',
      message: validationError,
    });
  }

  try {
    const response = await fetch(
      getDocumentsApiUrl(`${DOCUMENTS_API_PATH}/mandates`),
      buildJsonMutationOptions({
        ...entitySelectionPayload(form),
        clientId: Number(form.clientId),
        client: form.client,
        firmatario: form.firmatario,
        firmatarioRuolo: form.firmatarioRuolo,
        tipoCrediti: form.tipoCrediti,
        statoCrediti: form.statoCrediti,
        valoreNominale: form.valoreNominale,
        valoreStimato: form.valoreStimato,
        commissionePercentuale: form.commissionePercentuale,
        dataInizio: form.dataInizio,
        dataScadenza: form.dataScadenza,
        accettazionePrivacy: Boolean(form.accettazionePrivacy),
        accettazioneTermini: Boolean(form.accettazioneTermini),
        modalitaFirma: form.modalitaFirma,
        luogoFirma: form.luogoFirma,
        ...(Object.prototype.hasOwnProperty.call(form, 'customFields') ? { customFields: form.customFields } : {}),
      })
    );

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENTS_MODULE_DISABLED',
        message: 'Modulo documenti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildAuthFailureResult(tenantDbPayload);
    }

    if (!response.ok) {
      throw new Error(`DOCUMENTS_API_CREATE_MANDATE_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        document: mapApiDocumentToDocument(payload),
      },
      message: 'Mandato creato con successo!',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENTS_API_CREATE_MANDATE_UNAVAILABLE',
      message: 'Backend documenti non disponibile. Creazione mandato non completata.',
    });
  }
}

export async function createContractDocument(form) {
  const validationError = validateContractForm(form);
  if (validationError) {
    return buildMutationResult({
      ok: false,
      error: 'CONTRACT_VALIDATION_ERROR',
      message: validationError,
    });
  }

  try {
    const response = await fetch(
      getDocumentsApiUrl(`${DOCUMENTS_API_PATH}/contracts`),
      buildJsonMutationOptions({
        ...entitySelectionPayload(form),
        clientId: Number(form.clientId),
        client: form.client,
        dataContratto: form.dataContratto,
        luogoContratto: form.luogoContratto,
        firmatario: form.firmatario,
        firmatarioRuolo: form.firmatarioRuolo,
        righeCrediti: form.righeCrediti.map((row) => ({
          tipoCredito: row.tipoCredito,
          anno: row.anno,
          descrizione: row.descrizione,
          valoreNominale: row.valoreNominale,
          prezzoAcquisto: row.prezzoAcquisto,
        })),
        tranche: form.tranche.map((row) => ({
          percentuale: row.percentuale,
          importo: row.importo,
          dataScadenza: row.dataScadenza,
          condizione: row.condizione,
        })),
        iban: form.iban,
        nomeBanca: form.nomeBanca,
        dichiarazioneTitolarita: Boolean(form.dichiarazioneTitolarita),
        dichiarazioneAssenzaGravami: Boolean(form.dichiarazioneAssenzaGravami),
        accettazione1341_1: Boolean(form.accettazione1341_1),
        accettazione1341_2: Boolean(form.accettazione1341_2),
        ...(Object.prototype.hasOwnProperty.call(form, 'customFields') ? { customFields: form.customFields } : {}),
      })
    );

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'DOCUMENTS_MODULE_DISABLED',
        message: 'Modulo documenti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      return buildAuthFailureResult(tenantDbPayload);
    }

    if (!response.ok) {
      throw new Error(`DOCUMENTS_API_CREATE_CONTRACT_FAILED_${response.status}`);
    }

    const payload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        document: mapApiDocumentToDocument(payload),
      },
      message: 'Contratto creato con successo!',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'DOCUMENTS_API_CREATE_CONTRACT_UNAVAILABLE',
      message: 'Backend documenti non disponibile. Creazione contratto non completata.',
    });
  }
}

export async function getDocumentsOverview() {
  return {
    documents: await getDocuments(),
    clients: [],
    staff: [],
  };
}
